import { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Button, Typography, CircularProgress, Chip, Divider } from "@mui/material";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useTheme } from "@mui/material/styles";
import AppDialog from "../General/Dialog/AppDialog";
import OnlineQueryTooltip, { useOnlineQueryGate } from "../General/OnlineQueryGate";
import { useToast } from "../../context/ToastContext";
import { getLiquidationSheet, syncLiquidationSheet } from "../../services/IndividualLiquidationService";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";

function fmtDate(v) {
  if (!v) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) return `${+m[3]}/${+m[2]}/${m[1]}`;
  return new Date(v).toLocaleDateString("es-AR");
}

// "$ 480.868,76" → 480868.76. Los montos de la API vienen como texto formateado.
function parsePesos(s) {
  if (s == null) return null;
  const n = Number(String(s).replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

const CATEGORY_BY_TIPO = { Capital: "capital", Gastos: "gastos", Honorarios: "honorarios" };

const norm = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

// El portal a veces manda la "descripción" como texto real (ej. "Honorarios
// Dr. Mate Palou") y a veces como un placeholder sin info ("." o vacío, o
// repitiendo el propio tipoConcepto). Con placeholder, lo único que identifica
// el concepto es el tipo genérico — ahí se exige nombre EXACTO contra el
// catálogo/rubro (si no, "Honorarios" genérico matchearía de más contra
// "Honorarios de 2ª instancia" por simple substring). Con texto real se usa
// fuzzy (substring en cualquier sentido), con mínimo de 3 caracteres para no
// matchear por una sola letra o un punto suelto.
const TRIVIAL_DESC = new Set(["", ".", "-", "—", "n/a", "s/d"]);

// Matchea un concepto del portal (solo "Monto" — un "tipoCalculo" de tasa,
// p. ej. "Tasa Pasiva", es un interés, y eso siempre se calcula adentro de
// JEX, no hay campo donde cargar un monto ya hecho) contra:
//  1) un rubro que el operador YA tiene en el formulario (mismo texto gana), o
//  2) si no hay ninguno agregado, un tipo del CATÁLOGO con nombre parecido —
//     ahí se puede ofrecer agregarlo como rubro nuevo con ese monto.
// "Gastos" (total genérico, sin un ítem de catálogo 1 a 1) nunca matchea ni
// en rows ni en catálogo, y queda siempre como "sin coincidencia".
function matchConcept(concept, rows, available) {
  if (concept.tipoCalculo !== "Monto") return { row: null, catalogRubro: null };
  const category = CATEGORY_BY_TIPO[concept.tipoConcepto];
  if (!category) return { row: null, catalogRubro: null };

  const rawDesc = norm(concept.descripcion);
  const tipoNorm = norm(concept.tipoConcepto);
  const isTrivial = TRIVIAL_DESC.has(rawDesc) || rawDesc === tipoNorm;
  const desc = isTrivial ? tipoNorm : rawDesc;
  const matchFn = isTrivial
    ? (name) => norm(name) === desc
    : (name) => {
        const rname = norm(name);
        return rname && desc.length >= 3 && (desc.includes(rname) || rname.includes(desc));
      };

  const row = (rows || []).find((r) => r.category === category && matchFn(r.name)) || null;
  if (row) return { row, catalogRubro: null };

  const catalogRubro = (available || []).find((o) => o.category === category && matchFn(o.name)) || null;
  return { row: null, catalogRubro };
}

/**
 * Última planilla de liquidación ya registrada en el portal judicial
 * (MarcoPoloNet → Radiografía) para este expediente — distinta de las
 * liquidaciones hechas con JEX. Permite ver todos los conceptos (Capital /
 * Gastos / Honorarios, cada uno con su Monto o Interés) y, para los que
 * coincidan por nombre con un rubro ya cargado en el formulario o con un tipo
 * del catálogo, aplicar el monto automáticamente — agregando el rubro nuevo
 * si todavía no estaba. Los que no coinciden con nada (ej. "Gastos" total)
 * NO se agregan solos — quedan visibles para que el operador los use de guía.
 */
export default function LiquidationSheetDialog({ open, onClose, legalFileId, legalFileCode, rows, available, onApply }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { showToast } = useToast();
  const { online } = useOnlineQueryGate();

  const [sheet, setSheet] = useState(null);
  const [synced, setSynced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sessionRequiredLawyer, setSessionRequiredLawyer] = useState(null);

  const load = useCallback(async () => {
    if (!legalFileId) return;
    setLoading(true);
    try {
      const res = await getLiquidationSheet(legalFileId);
      setSheet(res.data?.data ?? null);
      setSynced(!!res.data?.synced);
    } catch {
      showToast("No se pudo cargar la planilla de liquidación del portal.", "error");
    } finally {
      setLoading(false);
    }
  }, [legalFileId, showToast]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncLiquidationSheet(legalFileId);
      setSheet(res.data?.data ?? null);
      setSynced(true);
      if (res.data?.message) {
        showToast(res.data.message, "info");
      } else {
        showToast("Planilla de liquidación actualizada desde el portal.", "success");
      }
    } catch (e) {
      if (e?.response?.data?.code === "judicial_session_required") {
        setSessionRequiredLawyer(e.response.data.lawyer || {});
      } else {
        showToast(
          e?.response?.data?.message ?? "No se pudo consultar la planilla de liquidación en el portal judicial.",
          "error",
        );
      }
    } finally {
      setSyncing(false);
    }
  };

  const matches = useMemo(
    () =>
      (sheet?.conceptos ?? []).map((c) => {
        const { row, catalogRubro } = matchConcept(c, rows, available);
        return { concepto: c, row, catalogRubro, monto: parsePesos(c.monto) };
      }),
    [sheet, rows, available],
  );
  const applicableCount = matches.filter((m) => (m.row || m.catalogRubro) && m.monto != null).length;

  const handleApply = () => {
    const actions = matches
      .filter((m) => (m.row || m.catalogRubro) && m.monto != null)
      .map((m) =>
        m.row
          ? { kind: "patch", id: m.row.id, amount: m.monto }
          : { kind: "add", rubro: m.catalogRubro, amount: m.monto },
      );
    if (!actions.length) return;
    onApply(actions);
    showToast(`${actions.length} rubro(s) cargado(s) con el monto del portal.`, "success");
  };

  return (
    <>
      <LawyerSessionRequiredDialog
        open={Boolean(sessionRequiredLawyer)}
        onClose={() => setSessionRequiredLawyer(null)}
        lawyer={sessionRequiredLawyer}
        onCaptured={() => handleSync()}
      />

      <AppDialog
        open={open}
        onClose={onClose}
        title="Planilla de liquidación del portal"
        subtitle={legalFileCode ? `Expediente ${legalFileCode} · portal judicial` : "Portal judicial"}
        icon={<DescriptionRoundedIcon />}
        maxWidth="sm"
        fullWidth
        stickyHeader
        actions={
          <>
            <OnlineQueryTooltip show={!online}>
              <Button
                onClick={handleSync}
                disabled={syncing || loading || !online}
                startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <CloudSyncRoundedIcon />}
                sx={{ mr: "auto", borderRadius: 2, fontWeight: 800 }}
              >
                {syncing ? "Consultando…" : "Actualizar desde el portal"}
              </Button>
            </OnlineQueryTooltip>
            <Button onClick={onClose} disabled={syncing} sx={{ borderRadius: 2 }}>
              Cerrar
            </Button>
          </>
        }
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : !sheet ? (
          <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
            <DescriptionRoundedIcon sx={{ fontSize: 34, opacity: 0.25, mb: 1 }} />
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
              {synced ? "Sin planilla de liquidación" : "Todavía no se consultó"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.65 }}>
              {synced
                ? "Este expediente no tiene planillas de liquidación cargadas en el portal judicial."
                : "Usá «Actualizar desde el portal» para traer la última planilla de liquidación de este expediente."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
                background: isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.6)",
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                Planilla N° {sheet.numero_planilla ?? "—"}
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.6 }}>
                {sheet.descripcion ? `${sheet.descripcion} · ` : ""}
                Consultada el {fmtDate(sheet.fetched_at)}
              </Typography>
            </Box>

            {onApply && applicableCount > 0 && (
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isDark ? "rgba(46,125,50,0.4)" : "rgba(46,125,50,0.35)",
                  background: isDark ? "rgba(46,125,50,0.10)" : "rgba(46,125,50,0.06)",
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: 13, mb: 1 }}>
                  {applicableCount} rubro(s) listos para cargar (coinciden por nombre con uno ya agregado o con el
                  catálogo)
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<PlaylistAddCheckRoundedIcon sx={{ fontSize: 17 }} />}
                  onClick={handleApply}
                  sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
                >
                  Aplicar montos coincidentes
                </Button>
              </Box>
            )}

            <Divider />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {matches.map(({ concepto: c, row, catalogRubro }, i) => (
                <Box
                  key={c.idConceptoDetalle ?? i}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.3 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.5 }}>
                      {c.tipoConcepto} · {c.tipoCalculo}
                    </Typography>
                    {row ? (
                      <Chip
                        size="small"
                        icon={<CheckCircleRoundedIcon sx={{ fontSize: 14 }} />}
                        label={`coincide con «${row.name}»`}
                        color="success"
                        variant="outlined"
                        sx={{ fontSize: 10.5, fontWeight: 700, height: 22 }}
                      />
                    ) : catalogRubro ? (
                      <Chip
                        size="small"
                        icon={<CheckCircleRoundedIcon sx={{ fontSize: 14 }} />}
                        label={`se agrega «${catalogRubro.name}»`}
                        color="info"
                        variant="outlined"
                        sx={{ fontSize: 10.5, fontWeight: 700, height: 22 }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label="sin coincidencia"
                        variant="outlined"
                        sx={{ fontSize: 10.5, fontWeight: 700, height: 22, opacity: 0.6 }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.3 }}>{c.descripcion}</Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 11.5, opacity: 0.75 }}>Monto: {c.monto ?? "—"}</Typography>
                    <Typography sx={{ fontSize: 11.5, opacity: 0.75 }}>Abonado: {c.abonado ?? "—"}</Typography>
                    <Typography sx={{ fontSize: 11.5, opacity: 0.75 }}>Saldo: {c.saldo ?? "—"}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Typography sx={{ fontSize: 11, opacity: 0.5 }}>
              Los conceptos de tipo "Interés" nunca se aplican automáticamente — JEX calcula el interés de cada
              rubro por tasa y fechas, no hay un campo donde cargar un monto ya calculado. Quedan acá como
              referencia para guiarte.
            </Typography>
          </Box>
        )}
      </AppDialog>
    </>
  );
}
